import api from './api';
import type { Product } from '../types';
import type { VatInvoiceType } from '../utils/mallSalePrice';

export type ServerCartItem = {
  id: number;
  product: Product;
  quantity: number;
  effectivePrice: number;
  productVariantId: number | null;
  variantLabel: string | null;
  specRemark: string | null;
  vatInvoiceType: VatInvoiceType | null;
  addedAt: number;
  unavailableReason: string | null;
};

export type CartListResponse = {
  items: ServerCartItem[];
  imported?: boolean;
};

export type CartWritePayload = {
  product_id: number;
  quantity: number;
  product_variant_id?: number | null;
  vat_invoice_type?: VatInvoiceType | null;
  spec_remark?: string | null;
};

const cartService = {
  async list(): Promise<CartListResponse> {
    const response = await api.get<CartListResponse>('/cart');
    return response.data;
  },

  async add(payload: CartWritePayload): Promise<CartListResponse> {
    const response = await api.post<CartListResponse>('/cart/items', payload);
    return response.data;
  },

  async update(
    id: number,
    payload: Partial<Pick<CartWritePayload, 'quantity' | 'vat_invoice_type' | 'spec_remark'>>,
  ): Promise<CartListResponse> {
    const response = await api.patch<CartListResponse>(`/cart/items/${id}`, payload);
    return response.data;
  },

  async remove(id: number): Promise<CartListResponse> {
    const response = await api.delete<CartListResponse>(`/cart/items/${id}`);
    return response.data;
  },

  async removeMany(ids: number[]): Promise<CartListResponse> {
    const response = await api.post<CartListResponse>('/cart/items/delete', { ids });
    return response.data;
  },

  async clear(): Promise<CartListResponse> {
    const response = await api.delete<CartListResponse>('/cart');
    return response.data;
  },

  async importLocal(payload: {
    import_token: string;
    items: CartWritePayload[];
  }): Promise<CartListResponse> {
    const response = await api.post<CartListResponse>('/cart/import-local', payload);
    return response.data;
  },
};

export default cartService;
